import React, { useEffect, useRef, useState } from "react";
import { Button, Column, Row } from "carbon-components-react";
import { Save16 } from "@carbon/icons-react";
import Card from "../../components/Card/Card";
import http from "../../http-common";
import { toast } from "react-toastify";

// Date-only fields: a period starts at 00:00:00 of the start day and ends at
// 23:59:59 of the end day (same semantics as the old hardcoded "21 Nov
// 23:59:59" deadline). datetime-local was abandoned on purpose - its value
// stays "" until BOTH date and time segments are filled, so admins who picked
// a date but left the time as "--:--" were silently saving null.
const apiDateToInputValue = (apiDate) => {
  if (!apiDate) return "";
  const [day, month, year] = apiDate.split(" ")[0].split("/");
  return `${year}-${month}-${day}`;
};

const INPUT_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const inputValueToApiDate = (inputValue, timeOfDay) => {
  const match = INPUT_DATE_PATTERN.exec(inputValue);
  if (!match) return null;
  const [, year, month, day] = match;
  return `${day}/${month}/${year} ${timeOfDay}`;
};

const SubmissionPeriodPage = () => {
  const [periods, setPeriods] = useState([]);
  const [isSaving, setIsSaving] = useState(null);
  // Values are read straight from the DOM at save time (uncontrolled inputs):
  // native date inputs fire onChange with "" for partially filled values, so
  // controlled state is not a reliable source of truth across browsers.
  const inputRefs = useRef({});

  const fetchPeriods = async () => {
    try {
      const response = await http.get("/admin/submission-periods");
      setPeriods(
        response.data.map((period) => ({
          ...period,
          startDate: apiDateToInputValue(period.startDate),
          endDate: apiDateToInputValue(period.endDate),
        }))
      );
    } catch (error) {
      toast.error("Erro ao carregar os períodos de submissão");
    }
  };

  useEffect(() => {
    fetchPeriods();
  }, []);

  const readDateField = (windowCode, field) => {
    const element = inputRefs.current[`${windowCode}-${field}`];
    if (!element) return null;

    const label = field === "start" ? "Início" : "Fim";

    // A partially filled native date field reports value "" but flags
    // badInput - distinguish it from a deliberately empty (no limit) field.
    if (element.validity && element.validity.badInput) {
      throw new Error(`O campo "${label}" está incompleto. Preencha a data completa ou limpe o campo.`);
    }

    const rawValue = element.value.trim();
    if (rawValue && !INPUT_DATE_PATTERN.test(rawValue)) {
      throw new Error(`Data inválida no campo "${label}". Use o seletor de data do navegador.`);
    }

    return inputValueToApiDate(rawValue, field === "start" ? "00:00:00" : "23:59:59");
  };

  const handleSave = async (period) => {
    let startDate;
    let endDate;
    try {
      startDate = readDateField(period.windowCode, "start");
      endDate = readDateField(period.windowCode, "end");
    } catch (validationError) {
      toast.error(validationError.message);
      return;
    }

    try {
      setIsSaving(period.windowCode);
      const response = await http.put(
        `/admin/submission-periods/${period.windowCode}`,
        { startDate, endDate }
      );
      // Echo back what the API actually persisted, so a silent null can
      // never masquerade as a successful save again.
      const saved = response.data;
      toast.success(
        `Período da ${period.windowDesc} actualizado. ` +
          `Início: ${saved.startDate || "sem limite"}. Fim: ${saved.endDate || "sem limite"}.`
      );
      fetchPeriods();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao guardar o período");
    } finally {
      setIsSaving(null);
    }
  };

  return (
    <Card title="Períodos de Submissão">
      <p className="mb-2">
        Defina o início e o fim do período em que os candidatos podem submeter o plano
        de negócio de cada janela. O início conta a partir das 00:00 e o fim até às
        23:59 do dia escolhido. Deixe em branco para não aplicar limite nessa data.
      </p>

      {periods.map((period) => (
        <div key={period.windowCode} style={{ marginTop: "2rem" }}>
          <h4>{period.windowDesc}</h4>
          <Row style={{ marginTop: "0.5rem" }}>
            <Column sm={6} md={4} lg={4}>
              <div className="bx--form-item">
                <label htmlFor={`start-${period.windowCode}`} className="bx--label">
                  Início (a partir das 00:00)
                </label>
                <input
                  id={`start-${period.windowCode}`}
                  type="date"
                  className="bx--text-input"
                  defaultValue={period.startDate}
                  ref={(el) => {
                    inputRefs.current[`${period.windowCode}-start`] = el;
                  }}
                />
              </div>
            </Column>
            <Column sm={6} md={4} lg={4}>
              <div className="bx--form-item">
                <label htmlFor={`end-${period.windowCode}`} className="bx--label">
                  Fim (até às 23:59)
                </label>
                <input
                  id={`end-${period.windowCode}`}
                  type="date"
                  className="bx--text-input"
                  defaultValue={period.endDate}
                  ref={(el) => {
                    inputRefs.current[`${period.windowCode}-end`] = el;
                  }}
                />
              </div>
            </Column>
          </Row>
          <Button
            renderIcon={Save16}
            style={{ marginTop: "1rem" }}
            disabled={isSaving === period.windowCode}
            onClick={() => handleSave(period)}
          >
            Guardar {period.windowDesc}
          </Button>
        </div>
      ))}
    </Card>
  );
};

export default SubmissionPeriodPage;
